import React from 'react';

/**
 * 몹 한 칸(속성×각성)의 능력치.
 *
 * SWARFARM 에서 몹을 받아올 때 같이 들어오는 값이다(`backend/src/utils/monsterSync.ts`).
 * 사람이 손대는 값이 아니라 [몬스터 업데이트] 때마다 최신으로 덮인다 — 밸런스 패치가
 * 있으면 여기 숫자가 따라 바뀐다.
 */
export interface MonsterStatsData {
    archetype: string | null;
    speed: number | null;
    hp: number | null;
    attack: number | null;
    defense: number | null;
    maxHp: number | null;
    maxAttack: number | null;
    maxDefense: number | null;
    critRate: number | null;
    critDamage: number | null;
    resistance: number | null;
    accuracy: number | null;
    leader?: { attribute: string; amount: number; area: string; element: string | null } | null;
    skills?: Array<{ id: number; slot: number; name: string; cooltime: number | null; passive: boolean; gauge: number; gaugeNote: string; speedUp: boolean }>;
}

/** SWARFARM 표기 → 게임에서 쓰는 말 */
const LEADER_ATTR: Record<string, string> = {
    'Attack Speed': '공격속도', 'Attack Power': '공격력', HP: '체력', Defense: '방어력',
    'Critical Rate': '치명확률', Accuracy: '효과적중', Resistance: '효과저항'
};
const LEADER_AREA: Record<string, string> = {
    General: '전체', Guild: '길드전', Arena: '아레나', Dungeon: '던전', Element: '속성'
};
const LEADER_ELEM: Record<string, string> = { Water: '물', Fire: '불', Wind: '풍', Light: '빛', Dark: '암' };

/** 리더 스킬을 한 줄로 — 예: "길드전 · 물 속성 공격속도 +28%" */
export function leaderText(l: MonsterStatsData['leader']): string | null {
    if (!l) return null;
    const where = LEADER_AREA[l.area] || l.area;
    const elem = l.element ? (LEADER_ELEM[l.element] || l.element) + ' 속성 ' : '';
    return `${where} · ${elem}${LEADER_ATTR[l.attribute] || l.attribute} +${l.amount}%`;
}

/** SWARFARM 의 archetype → 게임에서 쓰는 말 */
const ROLE: Record<string, string> = {
    Attack: '공격', Defense: '방어', HP: '체력', Support: '지원', Material: '재료'
};

/*
 * 게임 안 능력치 창을 본떴다.
 *
 * 검은 판에 **노란 이름 / 흰 숫자**, 체·공·방·공속을 먼저 묶고 한 칸 띄운 뒤 치명·효과 넷.
 * 게임에서 보던 자리 그대로라 어디를 봐야 할지 다시 배울 게 없다.
 *
 * 게임 창과 다른 점 하나: 초록색 덧붙는 수(룬·아티팩트로 오른 값)가 없다. 우리가 가진 건
 * **룬을 끼지 않은 기본값**뿐이라, 없는 값을 그리지 않는다.
 */
const GOLD = '#f0c040';
const panel: React.CSSProperties = {
    width: '100%', marginTop: 12, background: '#17181c', borderRadius: 12,
    padding: '12px 14px', border: '1px solid #2c2f36'
};
const row: React.CSSProperties = {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '3px 0'
};
const nameStyle: React.CSSProperties = {
    color: GOLD, fontSize: 14, fontWeight: 800, letterSpacing: '-0.2px'
};
const valueStyle: React.CSSProperties = {
    color: '#fff', fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums'
};

function Row({ name, v, suffix = '' }: { name: string; v: number | null; suffix?: string }) {
    return (
        <div style={row}>
            <span style={nameStyle}>{name}</span>
            <span style={valueStyle}>{v === null || v === undefined ? '-' : `${v}${suffix}`}</span>
        </div>
    );
}

export default function MonsterStats({ stats, stars }: { stats?: MonsterStatsData | null; stars?: number | null }) {
    if (!stats) {
        return (
            <div style={{ width: '100%', marginTop: 10, fontSize: 12, color: 'var(--text-sub, #78909c)', textAlign: 'center' }}>
                능력치 없음 — 앱에서 [몬스터 업데이트] 를 누르면 채워집니다
            </div>
        );
    }

    return (
        <div style={panel}>
            {/*
              머리줄 = 게임의 "Lv.40 ★★★★★★" 자리.

              **6성 만렙 기준이다.** 실측으로 확인했다 — 물 견주 2차각성(이카루)의 게임 화면이
              체력 9885 / 공격력 637 / 방어력 736 / 공격속도 108 인데, 우리 값이 그대로 같다.
              태생 성급은 3성이므로, SWARFARM 의 max_lvl_* 는 자연성급 만렙이 아니라 6성 만렙이다.
              그래서 별은 태생 성급이 아니라 여섯 개를 그린다(게임 화면과 같은 뜻이 되게).
              머리줄의 Lv.40 ★6 이 곧 '무슨 기준인지' 라, 아래에 따로 적던 줄은 걷어냈다.
            */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>Lv.40</span>
                <span style={{ color: '#ff8f1f', fontSize: 13, letterSpacing: 1 }}>{'★'.repeat(6)}</span>
                {!!stars && (
                    <span style={{ color: '#8a8f99', fontSize: 11 }}>태 {stars}성</span>
                )}
                {stats.archetype && (
                    <span style={{ marginLeft: 'auto', color: GOLD, fontSize: 11, border: `1px solid ${GOLD}`, borderRadius: 999, padding: '1px 9px' }}>
                        {ROLE[stats.archetype] || stats.archetype}
                    </span>
                )}
            </div>

            <Row name="체력" v={stats.maxHp} />
            <Row name="공격력" v={stats.maxAttack} />
            <Row name="방어력" v={stats.maxDefense} />
            <Row name="공격속도" v={stats.speed} />

            <div style={{ height: 1, background: '#2c2f36', margin: '8px 0' }} />

            {stats.leader && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 6px' }}>
                    <span style={{ color: GOLD, fontSize: 13, fontWeight: 800 }}>리더</span>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{leaderText(stats.leader)}</span>
                </div>
            )}
            {!!stats.skills?.length && (
                <div style={{ padding: '2px 0 6px' }}>
                    {stats.skills.map((s) => (
                        <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0', fontSize: 12 }}>
                            <span style={{ color: GOLD, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.passive ? '패시브' : s.slot + '스킬'}</span>
                            <span style={{ color: '#ddd', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{s.name}</span>
                            <span style={{ color: '#8a8f99', whiteSpace: 'nowrap' }}>
                                {s.gauge > 0 && <span style={{ color: '#5cd96a' }}>게증 {s.gauge}% </span>}
                                {s.speedUp && <span style={{ color: '#3080ff' }}>속업 </span>}
                                {s.cooltime !== null ? '쿨 ' + s.cooltime : ''}
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <Row name="치명 확률" v={stats.critRate} suffix="%" />
            <Row name="치명 피해" v={stats.critDamage} suffix="%" />
            <Row name="효과 저항" v={stats.resistance} suffix="%" />
            <Row name="효과 적중" v={stats.accuracy} suffix="%" />

        </div>
    );
}
